import React, { createContext, useContext, useEffect, useCallback, ReactNode } from 'react'
import en from '../locales/en.json'

export type Locale = 'en' | 'fr' | 'es' | 'ru' | 'it' | 'de' | 'pt' | 'hy'

type Messages = Record<string, string>

// English is bundled as the fallback; other languages are separate chunks loaded on demand.
const LOCALE_LOADERS: Record<Exclude<Locale, 'en'>, () => Promise<{ default: Messages }>> = {
  fr: () => import(/* webpackChunkName: "locale-fr" */ '../locales/fr.json'),
  es: () => import(/* webpackChunkName: "locale-es" */ '../locales/es.json'),
  ru: () => import(/* webpackChunkName: "locale-ru" */ '../locales/ru.json'),
  it: () => import(/* webpackChunkName: "locale-it" */ '../locales/it.json'),
  de: () => import(/* webpackChunkName: "locale-de" */ '../locales/de.json'),
  pt: () => import(/* webpackChunkName: "locale-pt" */ '../locales/pt.json'),
  hy: () => import(/* webpackChunkName: "locale-hy" */ '../locales/hy.json'),
}

const STORAGE_KEY = 'brc-locale'

const LOCALE_FLAGS: Record<Locale, string> = {
  en: '🇬🇧',
  fr: '🇫🇷',
  es: '🇪🇸',
  ru: '🇷🇺',
  it: '🇮🇹',
  de: '🇩🇪',
  pt: '🇵🇹',
  hy: '🇦🇲',
}

const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  ru: 'Русский',
  it: 'Italiano',
  de: 'Deutsch',
  pt: 'Português',
  hy: 'Հայերեն',
}

interface LanguageContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, params?: Record<string, string>) => string
  flags: typeof LOCALE_FLAGS
  localeNames: typeof LOCALE_NAMES
}

const messagesMap: Partial<Record<Locale, Messages>> = { en }

const isLocale = (value: string | null): value is Locale =>
  value === 'en' || (value != null && value in LOCALE_LOADERS)

export async function loadLocale(locale: Locale): Promise<Messages> {
  const cached = messagesMap[locale]
  if (cached) return cached
  try {
    const mod = await LOCALE_LOADERS[locale as Exclude<Locale, 'en'>]()
    messagesMap[locale] = mod.default
    return mod.default
  } catch {
    return en
  }
}

const LanguageContext = createContext<LanguageContextType>({
  locale: 'en',
  setLocale: () => {},
  t: (key: string) => key,
  flags: LOCALE_FLAGS,
  localeNames: LOCALE_NAMES,
})

const DEFAULT_LOCALE: Locale = 'en'

export const getStoredLocale = (): Locale => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE
  const stored = localStorage.getItem(STORAGE_KEY)
  return isLocale(stored) ? stored : DEFAULT_LOCALE
}

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = React.useState<Locale>(getStoredLocale)
  const [messages, setMessages] = React.useState<Messages>(() => messagesMap[locale] || en)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, locale)
  }, [locale])

  useEffect(() => {
    let active = true
    loadLocale(locale).then((loaded) => {
      if (active) setMessages(loaded)
    })
    return () => {
      active = false
    }
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    if (!isLocale(next)) return
    // Switch once the language file is loaded, so the UI never flashes English.
    // Persist before re-render: pages refetch on locale change and the API reads it from storage.
    loadLocale(next).then(() => {
      localStorage.setItem(STORAGE_KEY, next)
      setLocaleState(next)
    })
  }, [])

  const t = useCallback((key: string, params?: Record<string, string>): string => {
    let value = messages[key] ?? (en as Messages)[key] ?? key
    if (params) {
      Object.keys(params).forEach((k) => {
        value = value.replace(new RegExp(`{{${k}}}`, 'g'), params[k])
      })
    }
    return value
  }, [messages])

  return (
    <LanguageContext.Provider
      value={{
        locale,
        setLocale,
        t,
        flags: LOCALE_FLAGS,
        localeNames: LOCALE_NAMES,
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

export const useTranslation = () => useContext(LanguageContext)
export { LOCALE_FLAGS, LOCALE_NAMES }

const ABILITY_STAGE_KEYS: Record<string, string> = {
  'just starting': 'progress.justStarting',
  'building confidence': 'progress.buildingConfidence',
  'growing steadily': 'progress.growingSteadily',
  'solid footing': 'progress.solidFooting',
  exploring: 'progress.exploring',
  beginner: 'progress.levelBeginner',
  growing: 'progress.levelGrowing',
  intermediate: 'progress.levelIntermediate',
  good: 'progress.levelGood',
  senior: 'progress.levelSenior',
  expert: 'progress.levelExpert',
}

export function localizeAbilityLabel(
  t: (key: string, params?: Record<string, string>) => string,
  raw?: string | null,
  level?: number,
): string {
  const text = (raw || '').trim()
  const match = text.match(/^Level\s+(\d+)(?:\s*[·•\-–]\s*(.+))?$/i)
  if (match) {
    const lv = match[1]
    const rest = (match[2] || '').trim()
    if (!rest) return t('progress.levelN', { level: lv })
    const stageKey = ABILITY_STAGE_KEYS[rest.toLowerCase()]
    const name = stageKey ? t(stageKey) : rest
    return t('progress.levelLine', { level: lv, name })
  }
  if (level != null) return t('progress.levelN', { level: String(level) })
  return text
}

export function practiceCta(
  t: (key: string, params?: Record<string, string>) => string,
  sessionType?: string | null,
): string {
  if (sessionType === 'placement') return t('play.startPlacement')
  if (sessionType === 'check_in') return t('play.checkIn')
  return t('hub.practiceThis')
}

export function recapHint(
  t: (key: string, params?: Record<string, string>) => string,
  recap?: {
    placement?: { level?: number } | null
    currentLevel?: number
    subject?: string
    dueCount?: number
  } | null,
): string {
  if (!recap) return ''
  if (recap.placement) {
    const level = String(recap.placement.level ?? recap.currentLevel ?? '')
    if ((recap.subject || '').toLowerCase() === 'geography') {
      return t('play.placementGeoHint', { level })
    }
    return t('play.placementGrowHint', { level })
  }
  if ((recap.dueCount ?? 0) > 0) {
    return t('play.topicsDue', { count: String(recap.dueCount) })
  }
  return t('play.nextReviewFromToday')
}
